/**
 * Adding a new notification:
 *
 * If the notification is a "basic notification" where you only need to
 * specify the type and text, you can add it to the basicNotificationMap.
 *
 * If it requires more complex content like a <Link> with some onClick behaviour,
 * you create the entire template and add it to the customNotificationMap.
 */
import type React from 'react';
import { connect } from 'react-redux';
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';
import { Banner, CloseButton } from '@zillow/constellation-10';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import ireneNotifications from '@rental-manager/notifications';
import renterNotifications from '@renter-hub/notifications';
import { closeNotificationOnClick } from '@common/actions/NotificationActions';
import userPersonas from '@common/constants/userPersonas';
import './index.scss';
import cx from 'classnames';
import { logError } from '@zg-rentals/log-error';

const notificationMap = {
  landlord: ireneNotifications,
  renter: renterNotifications,
};

interface INotificationContainerComponentProps {
  persona: string;
  notification?: {
    page?: Record<string, unknown>;
    show?: boolean;
    name?: string;
    extraArgs?: Record<string, unknown>;
  };
  dispatch: (...args: Array<any>) => any;
  className: string;
  modal?: boolean;
}

const NotificationContainerComponent: React.FC<React.PropsWithChildren<INotificationContainerComponentProps>> = ({
  className,
  persona,
  modal = false,
  notification = {},
  dispatch,
}) => {
  const forPageOrModal = modal ? 'modal' : 'page';
  const notificationObj = notification[forPageOrModal] || {};
  const { show, name, extraArgs } = notificationObj;
  const onClose = () => dispatch(closeNotificationOnClick());

  if (show) {
    if (notificationMap[persona][name]) {
      const { custom = false, component, type, text: getText } = notificationMap[persona][name];
      if (custom) {
        const CustomNotification = component;
        return (
          <>
            <CustomNotification
              className={cx('txtWrap', className)}
              onClose={onClose}
              dispatch={dispatch}
              {...extraArgs}
            />
            {persona === userPersonas.LANDLORD && <SpacerRow />}
          </>
        );
      }

      const text = getText({ ...extraArgs });
      if (persona === userPersonas.LANDLORD) {
        return (
          <>
            <Notification className={cx('txtWrap', className)} type={type} onClose={onClose}>
              {text}
            </Notification>
            {persona === userPersonas.LANDLORD && <SpacerRow />}
          </>
        );
      } else {
        return (
          <Banner tone={type} closeButton={<CloseButton onImpact />}>
            {text}
          </Banner>
        );
      }
    }

    // If notification doesn't exist, log to splunk and don't render anything.
    logError({
      error: new Error('Could not find notification.'),
      context: `notification: ${name}, persona: ${persona}`,
    });
  }
  return null;
};

NotificationContainerComponent.defaultProps = {
  persona: userPersonas.LANDLORD,
  notification: {
    page: {
      show: false,
      name: '',
      extraArgs: {},
    },
  },
  dispatch: () => {},
};

const mapStateToProps = (state, { persona }) => {
  if (state && state.payments && state.payments[persona]) {
    return {
      notification: state.payments[persona].notification,
    };
  }
  return {};
};

export { NotificationContainerComponent };
export default connect(mapStateToProps)(NotificationContainerComponent);
